// src/components/Teacher/AssignmentSubmissions.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { Assignment } from '../../types/assignmentTypes';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
interface Submission {
    studentId: string;
    studentName: string;
    storyId: string;
    storyTitle: string;
    status: 'not_started' | 'in_progress' | 'submitted' | 'returned';
    submittedAt?: Date;
    wordCount: number;
}
 
const statusConfig = {
    submitted:   { label: 'Submitted',   bg: C.secondaryContainer, color: C.secondary,        icon: '✅' },
    in_progress: { label: 'In Progress', bg: C.tertiaryContainer,  color: C.tertiary,          icon: '✏️' },
    returned:    { label: 'Returned',    bg: C.errorContainer,      color: C.error,             icon: '🔄' },
    not_started: { label: 'Not Started', bg: C.surfaceHigh,         color: C.onSurfaceVariant,  icon: '⚪' },
};
 
const AssignmentSubmissions: React.FC = () => {
    const navigate = useNavigate();
    const { classId, assignmentId } = useParams<{ classId: string; assignmentId: string }>();
    const { currentUser } = useAuth();
    const [assignment, setAssignment]   = useState<Assignment | null>(null);
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [loading, setLoading]         = useState(true);
    const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    // ── Data loading (unchanged) ──────────────────────────────────────────────
    useEffect(() => {
        const loadData = async () => {
            if (!currentUser || !classId || !assignmentId) return;
            try {
                // Load assignment
                const assignmentDoc = await getDoc(doc(db, 'assignments', assignmentId));
                if (!assignmentDoc.exists()) {
                    alert('Assignment not found');
                    navigate(`/teacher/class/${classId}`);
                    return;
                }
                setAssignment({ id: assignmentDoc.id, ...assignmentDoc.data() } as Assignment);
 
                // Load class for student list
                const classDoc = await getDoc(doc(db, 'classes', classId));
                if (!classDoc.exists()) { alert('Class not found'); navigate('/dashboard'); return; }
                const studentIds = Object.keys(classDoc.data().students || {});
 
                // Load submissions for each student
                const submissionPromises = studentIds.map(async (studentId) => {
                    const userDoc = await getDoc(doc(db, 'users', studentId));
                    const studentName = userDoc.exists() ? userDoc.data().displayName || 'Student' : 'Student';
 
                    const storiesSnap = await getDocs(query(
                        collection(db, 'stories'),
                        where('userId', '==', studentId),
                        where('assignmentId', '==', assignmentId)
                    ));
 
                    if (storiesSnap.empty) {
                        return { studentId, studentName, storyId: '', storyTitle: '', status: 'not_started' as const, wordCount: 0 };
                    }
 
                    const storyDoc = storiesSnap.docs[0];
                    const storyData = storyDoc.data();
                    const chapters = storyData.content?.chapters || [];
                    const wordCount = chapters.reduce((total: number, chapter: any) =>
                        total + (chapter.content?.split(/\s+/).filter(Boolean).length || 0), 0);
 
                    let status: Submission['status'] = 'in_progress';

// Check studentAssignments for accurate status
const statusDocId = `${studentId}_${assignmentId}`;
const statusDoc = await getDoc(doc(db, 'studentAssignments', statusDocId));
if (statusDoc.exists()) {
    status = statusDoc.data().status || 'in_progress';
} else if (storyData.status === 'published' && storyData.classId) {
    status = 'submitted';
} else if (storyData.status === 'returned') {
    status = 'returned';
}
 
                    return {
                        studentId, studentName,
                        storyId: storyDoc.id,
                        storyTitle: storyData.title || 'Untitled',
                        status, wordCount,
                        submittedAt: storyData.submittedAt?.toDate(),
                    };
                });
 
                setSubmissions(await Promise.all(submissionPromises));
            } catch (err) {
                console.error('Error loading assignment submissions:', err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [currentUser, classId, assignmentId, navigate]);
 
    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📝</div>
                    <div style={{ fontSize: '1rem', color: C.onSurfaceVariant, fontWeight: '600' }}>Loading submissions...</div>
                </div>
            </div>
        );
    }
 
    if (!assignment) return null;
 
    const submittedCount   = submissions.filter(s => s.status === 'submitted').length;
    const inProgressCount  = submissions.filter(s => s.status === 'in_progress').length;
    const notStartedCount  = submissions.filter(s => s.status === 'not_started').length;
    const totalStudents    = submissions.length;
    const submissionRate   = totalStudents > 0 ? Math.round((submittedCount / totalStudents) * 100) : 0;
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{
                marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
                padding: isMobile ? '20px 16px 80px' : '40px 48px',
                boxSizing: 'border-box', overflowX: 'hidden',
            }}>
 
                {/* ── Back + header ────────────────────────────────────────── */}
                <div style={{ marginBottom: '32px' }}>
                {/* ── Breadcrumb ───────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/dashboard')}
                        style={{ background: 'none', border: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                        Dashboard
                    </button>
                    <span style={{ color: C.outlineVariant, fontSize: '0.82rem' }}>›</span>
                    <button onClick={() => navigate(`/teacher/class/${classId}`)}
                        style={{ background: 'none', border: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                        Class
                    </button>
                    <span style={{ color: C.outlineVariant, fontSize: '0.82rem' }}>›</span>
                    <span style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, fontWeight: '600' }}>{assignment?.title || 'Assignment'}</span>
                </div>
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.8rem' : '2.2rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        {assignment.title}
                    </h1>
                    {assignment.description && (
                        <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 20px 0', lineHeight: '1.6' }}>
                            {assignment.description}
                        </p>
                    )}
 
                    {/* Due date */}
                    {(assignment as any).dueDate && (
                        <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, marginBottom: '20px' }}>
                            📅 Due {(assignment as any).dueDate.toDate().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                    )}
                </div>
 
                {/* ── Stat cards ───────────────────────────────────────────── */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '14px', marginBottom: '32px' }}>
                    {[
                        { label: 'Submitted',   value: submittedCount,  bg: C.secondaryContainer, color: C.secondary,       icon: '✅' },
                        { label: 'In Progress', value: inProgressCount, bg: C.tertiaryContainer,  color: C.tertiary,         icon: '✏️' },
                        { label: 'Not Started', value: notStartedCount, bg: C.surfaceHigh,        color: C.onSurfaceVariant, icon: '⚪' },
                        { label: 'Submit Rate', value: `${submissionRate}%`, bg: C.primaryContainer, color: C.primary,       icon: '📊' },
                    ].map(stat => (
                        <div key={stat.label} style={{ background: stat.bg, borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span style={{ fontSize: '1.4rem' }}>{stat.icon}</span>
                            <div style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: stat.color, lineHeight: 1 }}>{stat.value}</div>
                            <div style={{ fontSize: '0.7rem', fontWeight: '700', color: stat.color, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</div>
                        </div>
                    ))}
                </div>
 
                {/* ── Progress bar ─────────────────────────────────────────── */}
                <div style={{ background: C.surfaceLowest, borderRadius: '16px', padding: '20px 24px', marginBottom: '28px', border: `1px solid ${C.surfaceHigh}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '700', fontSize: '0.85rem', color: C.onBackground }}>Submission Progress</span>
                        <span style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, fontWeight: '600' }}>{submittedCount} of {totalStudents} students</span>
                    </div>
                    <div style={{ height: '10px', background: C.surfaceHigh, borderRadius: '5px', overflow: 'hidden' }}>
                        <div style={{ width: `${submissionRate}%`, height: '100%', background: `linear-gradient(90deg, ${C.secondary}, #34d399)`, borderRadius: '5px', transition: 'width 0.5s' }} />
                    </div>
                </div>
 
                {/* ── Submissions table ────────────────────────────────────── */}
                <div style={{ background: C.surfaceLowest, borderRadius: '20px', overflow: 'hidden', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.surfaceHigh}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.1rem', color: C.onBackground, margin: 0 }}>
                            Student Submissions
                        </h2>
                        <span style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, fontWeight: '500' }}>
                            {submissions.length} student{submissions.length !== 1 ? 's' : ''}
                        </span>
                    </div>
 
                    {isMobile ? (
                        /* Mobile: stacked cards */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: C.surfaceHigh }}>
                            {submissions.map(sub => {
                                const cfg = statusConfig[sub.status];
                                return (
                                    <div key={sub.studentId} style={{ background: C.surfaceLowest, padding: '16px 20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '800', fontSize: '0.9rem', flexShrink: 0 }}>
                                                    {sub.studentName.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '700', fontSize: '0.9rem', color: C.onBackground }}>{sub.studentName}</div>
                                                    <div style={{ fontSize: '0.78rem', color: sub.storyTitle ? C.onSurfaceVariant : C.outlineVariant, fontStyle: sub.storyTitle ? 'normal' : 'italic' }}>
                                                        {sub.storyTitle || 'No story yet'}
                                                    </div>
                                                </div>
                                            </div>
                                            <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                                {cfg.icon} {cfg.label}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.78rem', color: C.onSurfaceVariant }}>{sub.wordCount > 0 ? `${sub.wordCount.toLocaleString()} words` : 'No words yet'}</span>
                                            {sub.storyId && (
                                                <button
                                                    onClick={() => navigate(`/teacher/class/${classId}/story/${sub.storyId}`)}
                                                    style={{ background: C.primaryContainer, color: C.primary, border: 'none', borderRadius: '9999px', padding: '6px 14px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                                                >
                                                    View →
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* Desktop: table */
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.surfaceLow }}>
                                    {['Student', 'Story Title', 'Word Count', 'Status', ''].map((h, i) => (
                                        <th key={i} style={{ padding: '14px 20px', textAlign: i === 2 || i === 3 ? 'center' : i === 4 ? 'right' : 'left', fontSize: '0.72rem', fontWeight: '800', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'Epilogue', sans-serif" }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {submissions.map(sub => {
                                    const cfg = statusConfig[sub.status];
                                    return (
                                        <tr
                                            key={sub.studentId}
                                            style={{ borderBottom: `1px solid ${C.surfaceHigh}`, transition: 'background 0.15s' }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = C.surfaceLow; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                        >
                                            {/* Student */}
                                            <td style={{ padding: '16px 20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '800', fontSize: '0.9rem', flexShrink: 0 }}>
                                                        {sub.studentName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <span style={{ fontWeight: '700', fontSize: '0.9rem', color: C.onBackground }}>{sub.studentName}</span>
                                                </div>
                                            </td>
 
                                            {/* Story title */}
                                            <td style={{ padding: '16px 20px', fontSize: '0.875rem', color: sub.storyTitle ? C.onSurface : C.outlineVariant, fontStyle: sub.storyTitle ? 'normal' : 'italic' }}>
                                                {sub.storyTitle || 'No story yet'}
                                            </td>
 
                                            {/* Word count */}
                                            <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                                {sub.wordCount > 0 ? (
                                                    <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.primary }}>
                                                        {sub.wordCount.toLocaleString()}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: C.outlineVariant, fontSize: '0.875rem' }}>—</span>
                                                )}
                                            </td>
 
                                            {/* Status badge */}
                                            <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                                <span style={{ background: cfg.bg, color: cfg.color, padding: '4px 12px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                                    {cfg.icon} {cfg.label}
                                                </span>
                                            </td>
 
                                            {/* Action */}
                                            <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                                                {sub.storyId ? (
                                                    <button
                                                        onClick={() => navigate(`/teacher/class/${classId}/story/${sub.storyId}`)}
                                                        style={{ background: C.primaryContainer, color: C.primary, border: 'none', borderRadius: '9999px', padding: '7px 16px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = C.primaryContainer; e.currentTarget.style.color = C.primary; }}
                                                    >
                                                        View Story →
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: '0.78rem', color: C.outlineVariant }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
 
                    {submissions.length === 0 && (
                        <div style={{ padding: '48px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📭</div>
                            <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0, fontWeight: '600' }}>No students in this class yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
 
export default AssignmentSubmissions;